const IDBStore: any = require('../lib/idbstore');

import { IIndexable, IKeyValueAsynchronous } from 'core-ts-types';

type key = string;

export interface IHash<T> {
  [key: string]: T;
}

export interface ITable<T extends IIndexable> extends IKeyValueAsynchronous<T> {
  isOpen(): boolean;
  isClosed(): boolean; ready(): Promise<ITable<T>>;
  fail(): Promise<ITable<T>>;
  open(): Promise<ITable<T>>;
  close(): Promise<ITable<T>>;
  putAll(values: T[] | IHash<T>): Promise<ITable<T>>;
  delAll(values: T[] | IHash<T>): Promise<ITable<T>>;
  get(k: key): Promise<T>;
  read(): Promise<IHash<T>>;
}

interface ITableOptions {
  indexes?: {
    multiEntry: boolean;
    keyPath: string;
    unique: boolean;
    name: string;
  }[];
}

export interface ITableCallback<T extends IIndexable> {
  (table: Table<T>): void;
}

export interface ITableResult<T extends IIndexable> {
  (value: T): void;
}

export class Table<T extends IIndexable> implements ITable<T> {

  private static DATABASE_DEFAULTS: any = {
    implementation: 'indexedDB',
    autoIncrement: false,
    storePrefix: 'eab-',
    keyPath: 'id',
    dbVersion: 1
  };

  private _initialize: Promise<any>;
  private _isOpen: boolean = false;
  private _idb: any;

  constructor(location: string, options: ITableOptions = {}) {

    this._initialize = new Promise((resolve: () => void, reject: (error: Error) => void): void => {

      this._idb = new IDBStore(Object.assign(Table.DATABASE_DEFAULTS, {
        onStoreReady: resolve,
        onError: reject,
        storeName: location
      }, options));

    });

  }

  instance<T extends IIndexable>(location: string, options: ITableOptions = {}): ITable<T> {
    return new Table<T>(location, options);
  }

  static build<T extends IIndexable>(location: string, options: ITableOptions = {}): ITable<T> {
    return new Table<T>(location, options);
  }

  ready(): Promise<Table<T>> {
    return new Promise((resolve: ITableCallback<T>, reject: (error: Error) => void): void => {
      this._initialize
        .then((): void => {
          this._isOpen = true;
          resolve(this);
        });
    });
  }

  fail(): Promise<Table<T>> {
    return new Promise((resolve: ITableCallback<T>, reject: (error: Error) => void): void => {
      this._initialize
        .catch((error: Error): void => {
          this._isOpen = false;
          reject(error);
        });
    });
  }

  open(): Promise<Table<T>> {
    return this.ready();
  }

  close(): Promise<Table<T>> {
    return new Promise((resolve: ITableCallback<T>, reject: (error: Error) => void): void => {
      this._idb.deleteDatabase(() => {
        this._isOpen = false;
        resolve(this);
      }, reject);
    });
  }

  isOpen(): boolean {
    return this._isOpen === true;
  }

  isClosed(): boolean {
    return this._isOpen === false;
  }

  exists(k: key): boolean {
    return this._idb.hasIndex(k);
  }

  put(v: T): Promise<T> {
    return new Promise<T>((resolve: ITableResult<T>, reject: (error?: any) => void): void => {

      const interceptSuccess: Function = (id: key): void => {
        (v as any).id = id;
        resolve(v);
      };

      this._idb.put(v, interceptSuccess, reject);

    });
  }

  get(k: key): Promise<T> {
    return new Promise<T>((resolve: ITableResult<T>, reject: (error?: any) => void): void => {
      this._idb.get(k, resolve, reject);
    });
  }

  del(kOrV: T | string): Promise<string> {
    return new Promise<string>((resolve: (id: string) => void, reject: (error?: any) => void): void => {

      const id: string = (this._isIndex(kOrV))
        ? kOrV
        : (kOrV as any).id;

      this._idb.remove(id, () => resolve(id), reject);

    });
  }

  report(fn: (source: IHash<T>) => IHash<T>): Promise<IHash<T>> {
    return new Promise((resolve: (db: IHash<T>) => void, reject: (error: Error) => void): void => {
      this.read().then((_source: IHash<T>) => {
        resolve(fn(_source));
      });
    });
  }

  refresh(fn: (source: IHash<T>) => IHash<T>): Promise<IHash<T>> {
    return new Promise((resolve: (db: IHash<T>) => void, reject: (error: Error) => void): void => {
      this.read().then((_source: IHash<T>) => {
        this.write(fn(_source))
          .then(resolve)
          .catch(reject);
      });
    });
  }

  cursor(values: T[] | IHash<T>): T[] {
    console.debug('not implemented ITable.cursor');
    return [];
  }

  write(values: T[] | IHash<T>): Promise<IHash<T>> {
    return this.clear().then(() => {
      return this.putAll(values).then(() => {
        return (_.isArray(values))
          ? toHash(values)
          : values;
      });
    });
  }

  read(): Promise<IHash<T>> {
    return new Promise((resolve: (db: IHash<T>) => void, reject: (error: Error) => void): void => {
      this._idb.getAll((resultList: T[]) => {
        resolve(toHash(resultList));
      });
    });
  }

  clear(): Promise<this> {
    return new Promise((resolve: ITableCallback<T>, reject: (error: Error) => void): void => {
      this._idb.clear(resolve, reject);
    }).then(() => this);
  }

  putAll(values: T[] | IHash<T>): Promise<Table<T>> {
    return new Promise((resolve: (values: Table<T>) => void, reject: (error: Error) => void): void => {
      this._idb.putBatch(this._convertToBatch(values), (wasSuccessful: boolean): void => {
        resolve(this);
      }, reject);
    });
  }

  delAll(values: T[] | IHash<T>): Promise<Table<T>> {
    return new Promise((resolve: (values: Table<T>) => void, reject: (error: Error) => void): void => {
      this._idb.removeBatch(this._convertToBatch(values), (wasSuccessful: boolean): void => {
        resolve(this);
      }, reject);
    });
  }

  private _convertToBatch(values: T[] | IHash<T>): T[] {
    return (_.isArray(values))
      ? values as T[]
      : Object.keys(values).map((key: string): T => values[key]) as T[];
  }

  private _isIndex(k: key | T): boolean {
    return (typeof k === 'string' || typeof k === 'number');
  }

}

/**
 * @description convert an array to a hash -
 * @purpose to support adding arrays of objects into a NoSQL hash
 */
function toHash<T extends IIndexable>(values: T[] | IHash<T>): IHash<T> {

  if (!(values instanceof Array)) {
    return values as IHash<T>;
  }

  const hash: IHash<T> = {};

  let n: number = values.length;
  let o: T;

  while (n--) {
    o = values[n];
    hash[o.id] = o;
  }

  return hash;
}




// WEBPACK FOOTER //
// ./app/vendor/utility-ts-database/dist/core/table.ts